import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  AudioPlayer,
  CsvViewer,
  ImageViewer,
  JsonViewer,
  PdfViewer,
  TextViewer,
  VideoViewer,
  XmlFileViewer,
} from '.';
import { parseJson } from '@/utils';

export interface EventPayloadViewerProps {
  payload: any;
  label?: string;
  maxHeight?: number;
  showLabel?: boolean;
  showMimeType?: boolean;
  headerActions?: React.ReactNode;
  allowCopy?: boolean;
}

export interface Base64Document {
  content: string;
  type: string;
  mimeType: string;
  filename?: string;
}

export type ViewerKind = 'pdf' | 'image' | 'audio' | 'video' | 'csv' | 'xml' | 'json' | 'text';

/**
 * Decode Base64 string to UTF-8 text safely
 */
const decodeBase64ToText = (b64: string): string => {
  try {
    const binString = atob(b64);
    const bytes = Uint8Array.from(binString, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    try {
      return atob(b64);
    } catch {
      return b64;
    }
  }
};

/**
 * Check if an object matches the Base64Encoded document structure
 */
const isDocCandidate = (obj: any): Base64Document | null => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const type = obj.type || obj.payloadType || obj.payload_type;
  const isBase64 =
    typeof type === 'string' &&
    (type === 'Base64Encoded' ||
      type.toLowerCase() === 'base64encoded' ||
      type.toLowerCase() === 'base64_encoded' ||
      type.toLowerCase() === 'base64');
  const hasContent = typeof obj.content === 'string' && obj.content.length > 0;
  const mime = obj.mimeType || obj.payloadMimeType || obj.mime_type || obj.mimetype;
  const hasMime = typeof mime === 'string' && mime.length > 0;

  if (isBase64 && hasContent && hasMime) {
    return {
      content: obj.content,
      type: String(type),
      mimeType: String(mime),
      filename:
        obj.filename ||
        obj.fileName ||
        obj.name ||
        obj.item_name ||
        obj.itemName ||
        obj.document_title ||
        obj.documentTitle,
    };
  }
  return null;
};

/**
 * Robustly and recursively extract all Base64Documents from any level of nested JSON / objects
 */
export const extractAllBase64Documents = (
  val: any,
  maxDepth = 8,
  visited = new Set<any>(),
  results: Base64Document[] = [],
): Base64Document[] => {
  if (!val || maxDepth < 0) return results;

  let target = val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      const parsed = parseJson(trimmed);
      if (parsed) target = parsed;
      else return results;
    } else {
      return results;
    }
  }

  if (typeof target !== 'object' || target === null) return results;
  if (visited.has(target)) return results;
  visited.add(target);

  const direct = isDocCandidate(target);
  if (direct) {
    results.push(direct);
    return results;
  }

  if (Array.isArray(target)) {
    for (const item of target) {
      extractAllBase64Documents(item, maxDepth - 1, visited, results);
    }
    return results;
  }

  const parentFilename =
    target.filename ||
    target.item_name ||
    target.itemName ||
    target.document_title ||
    target.documentTitle ||
    target.fileName ||
    target.name;

  // Search priority keys first
  const priorityKeys = [
    'report_pdf',
    'reportPdf',
    'report',
    'document',
    'file',
    'pdf',
    'payload',
    'result',
    'data',
    'item',
    'output',
    'eventPayload',
    'event_payload',
  ];

  for (const key of priorityKeys) {
    if (key in target && target[key] !== undefined && target[key] !== null) {
      const beforeLen = results.length;
      extractAllBase64Documents(target[key], maxDepth - 1, visited, results);
      if (results.length > beforeLen && parentFilename) {
        for (let i = beforeLen; i < results.length; i++) {
          if (!results[i].filename) {
            results[i].filename = parentFilename;
          }
        }
      }
    }
  }

  // Search any remaining keys
  for (const key of Object.keys(target)) {
    if (!priorityKeys.includes(key) && target[key] !== undefined && target[key] !== null) {
      const beforeLen = results.length;
      extractAllBase64Documents(target[key], maxDepth - 1, visited, results);
      if (results.length > beforeLen && parentFilename) {
        for (let i = beforeLen; i < results.length; i++) {
          if (!results[i].filename) {
            results[i].filename = parentFilename;
          }
        }
      }
    }
  }

  return results;
};

/**
 * Extract the first Base64Document found recursively
 */
export const extractBase64Document = (val: any): Base64Document | null => {
  const docs = extractAllBase64Documents(val);
  return docs.length > 0 ? docs[0] : null;
};

/**
 * Determine if a viewer component is available for the given MIME type
 */
export const getViewerKindForMime = (mimeType: string): ViewerKind | null => {
  const cleanMime = (mimeType || '').split(';')[0].trim().toLowerCase();
  if (cleanMime === 'application/pdf') return 'pdf';
  if (cleanMime.startsWith('image/')) return 'image';
  if (cleanMime.startsWith('audio/')) return 'audio';
  if (cleanMime.startsWith('video/')) return 'video';
  if (cleanMime === 'text/csv' || cleanMime === 'application/csv') return 'csv';
  if (cleanMime.includes('xml')) return 'xml';
  if (cleanMime === 'application/json' || cleanMime.endsWith('+json')) return 'json';
  if (cleanMime.startsWith('text/')) return 'text';
  return null;
};

/**
 * Helper to test if a string looks like valid XML
 */
const isXmlString = (str: string): boolean => {
  const trimmed = str.trim();
  return (
    trimmed.startsWith('<') &&
    trimmed.endsWith('>') &&
    (trimmed.startsWith('<?xml') ||
      trimmed.startsWith('<root') ||
      trimmed.startsWith('<document') ||
      /<\/[a-zA-Z0-9_:-]+>$/.test(trimmed))
  );
};

/**
 * Helper to test if a string looks like CSV
 */
const isCsvString = (str: string): boolean => {
  const lines = str.trim().split('\n');
  if (lines.length < 2) return false;
  const firstLineCols = lines[0].split(',').length;
  if (firstLineCols > 1 && lines[1].split(',').length === firstLineCols) return true;
  const firstLineTabs = lines[0].split('\t').length;
  if (firstLineTabs > 1 && lines[1].split('\t').length === firstLineTabs) return true;
  return false;
};

/**
 * Universal Event Payload Viewer that extracts content,
 * identifies payloadMimeType (hiding internal payloadType),
 * and dispatches to the most appropriate viewer.
 */
export const EventPayloadViewer: React.FC<EventPayloadViewerProps> = ({
  payload: rawInput,
  label = 'Output Payload',
  maxHeight = 0,
  showLabel = true,
  showMimeType = true,
  headerActions,
  allowCopy = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'document' | 'raw'>('document');
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);

  const allDocuments = useMemo(() => extractAllBase64Documents(rawInput), [rawInput]);

  useEffect(() => {
    setSelectedDocIndex(0);
  }, [rawInput]);

  const activeDoc = allDocuments[selectedDocIndex] || allDocuments[0] || null;

  const {
    detectedDocument,
    isDocumentViewable,
    resolvedContent,
    resolvedMimeType,
    viewerKind,
    filename,
  } = useMemo(() => {
    if (rawInput === null || rawInput === undefined) {
      return {
        detectedDocument: null,
        isDocumentViewable: false,
        resolvedContent: '',
        resolvedMimeType: 'text/plain',
        viewerKind: 'text' as ViewerKind,
        filename: undefined,
      };
    }

    // 1. Check if the input contains a Base64Document structure
    const doc = activeDoc;
    if (doc) {
      const docViewerKind = getViewerKindForMime(doc.mimeType);
      const isViewable = docViewerKind !== null;

      if (isViewable) {
        let content: any = '';
        if (
          docViewerKind === 'pdf' ||
          docViewerKind === 'image' ||
          docViewerKind === 'audio' ||
          docViewerKind === 'video'
        ) {
          const cleanMime = doc.mimeType.split(';')[0].trim();
          content = doc.content.startsWith('data:')
            ? doc.content
            : `data:${cleanMime};base64,${doc.content}`;
        } else if (docViewerKind === 'json') {
          const decoded = decodeBase64ToText(doc.content);
          content = parseJson(decoded) || decoded;
        } else {
          // csv, xml, text
          content = decodeBase64ToText(doc.content);
        }

        return {
          detectedDocument: doc,
          isDocumentViewable: true,
          resolvedContent: content,
          resolvedMimeType: doc.mimeType,
          viewerKind: docViewerKind,
          filename: doc.filename,
        };
      } else {
        // Base64 document detected, but no inline viewer available
        return {
          detectedDocument: doc,
          isDocumentViewable: false,
          resolvedContent: rawInput,
          resolvedMimeType: doc.mimeType,
          viewerKind: 'json' as ViewerKind,
          filename: doc.filename,
        };
      }
    }

    // 2. Fallback handling for standard payloads (EventDataPayload, BASE64_BYTES, TEXT_EXTRACT, etc.)
    let content = rawInput;
    let payloadType: string | undefined = undefined;
    let payloadMimeType: string | undefined = undefined;

    // Unpack EventDataPayload structure if present
    if (
      typeof rawInput === 'object' &&
      rawInput !== null &&
      ('payload' in rawInput || 'payloadType' in rawInput || 'payload_type' in rawInput)
    ) {
      content = rawInput.payload !== undefined ? rawInput.payload : rawInput;
      payloadType = rawInput.payloadType || rawInput.payload_type;
      payloadMimeType = rawInput.payloadMimeType || rawInput.payload_mime_type;
    }

    // Handle BASE64_BYTES
    if (payloadType === 'BASE64_BYTES' && typeof content === 'string') {
      if (payloadMimeType === 'application/json') {
        try {
          content = parseJson(atob(content));
        } catch {
          // fallback to raw
        }
      } else if (!content.startsWith('data:') && payloadMimeType) {
        content = `data:${payloadMimeType};base64,${content}`;
      }
    }

    // Handle TEXT_EXTRACT
    if (payloadType === 'TEXT_EXTRACT') {
      if (typeof content === 'object' && content !== null && 'textContent' in content) {
        content = content.textContent;
      } else if (typeof content === 'string') {
        const parsed = parseJson(content);
        if (parsed && typeof parsed === 'object' && 'textContent' in parsed) {
          content = parsed.textContent;
        }
      }
      if (!payloadMimeType) payloadMimeType = 'text/plain';
    }

    // Handle OBJECT_JSON / DATASOURCE_URL
    if (payloadType === 'OBJECT_JSON' || payloadType === 'DATASOURCE_URL') {
      if (!payloadMimeType) payloadMimeType = 'application/json';
      if (typeof content === 'string') {
        const parsed = parseJson(content);
        if (parsed !== null && typeof parsed === 'object') {
          content = parsed;
        }
      }
    }

    // Handle XML type
    if (payloadType === 'XML') {
      if (!payloadMimeType) payloadMimeType = 'application/xml';
    }

    // Extract MIME from data URL if applicable
    if (typeof content === 'string' && content.startsWith('data:')) {
      const match = content.match(/^data:([^;,]+)/);
      if (match && match[1]) {
        payloadMimeType = match[1];
      }
    }

    // Infer MIME type if still missing
    if (!payloadMimeType) {
      if (typeof content === 'object' && content !== null) {
        payloadMimeType = 'application/json';
      } else if (typeof content === 'string') {
        const trimmed = content.trim();
        if (isXmlString(trimmed)) {
          payloadMimeType = 'application/xml';
        } else if (
          (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))
        ) {
          const parsed = parseJson(trimmed);
          if (parsed && typeof parsed === 'object') {
            content = parsed;
            payloadMimeType = 'application/json';
          } else {
            payloadMimeType = 'text/plain';
          }
        } else if (isCsvString(trimmed)) {
          payloadMimeType = 'text/csv';
        } else {
          payloadMimeType = 'text/plain';
        }
      } else {
        payloadMimeType = 'text/plain';
      }
    }

    // Determine viewer kind based on resolved MIME type
    const lowerMime = (payloadMimeType || '').toLowerCase();
    let viewerKind: ViewerKind = 'text';

    if (lowerMime === 'application/pdf') {
      viewerKind = 'pdf';
    } else if (lowerMime.startsWith('image/')) {
      viewerKind = 'image';
    } else if (lowerMime.startsWith('audio/')) {
      viewerKind = 'audio';
    } else if (lowerMime.startsWith('video/')) {
      viewerKind = 'video';
    } else if (lowerMime.includes('csv')) {
      viewerKind = 'csv';
    } else if (lowerMime.includes('xml')) {
      viewerKind = 'xml';
    } else if (lowerMime === 'application/json' || (typeof content === 'object' && content !== null)) {
      viewerKind = 'json';
    } else {
      viewerKind = 'text';
    }

    return {
      detectedDocument: null,
      isDocumentViewable: false,
      resolvedContent: content,
      resolvedMimeType: payloadMimeType,
      viewerKind,
      filename: undefined,
    };
  }, [rawInput, activeDoc]);

  const handleDownload = () => {
    if (!detectedDocument) return;
    const cleanMime = detectedDocument.mimeType.split(';')[0].trim();
    const dataUrl = detectedDocument.content.startsWith('data:')
      ? detectedDocument.content
      : `data:${cleanMime};base64,${detectedDocument.content}`;
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename || `document_${Date.now()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopy = () => {
    const textToCopy =
      typeof resolvedContent === 'object'
        ? JSON.stringify(resolvedContent, null, 2)
        : String(resolvedContent ?? '');
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        p: 1.25,
        bgcolor: '#f8fafc',
        borderRadius: 1,
        border: '1px solid #e2e8f0',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Header with Title, filename badge, payloadMimeType badge, and actions */}
      {(showLabel || showMimeType || headerActions || allowCopy || detectedDocument) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
          gap={1}
          sx={{ mb: 0.75, width: '100%', minWidth: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
            {showLabel && label && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.75rem', display: 'inline-block' }}
              >
                {label}
              </Typography>
            )}
            {allDocuments.length > 1 ? (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                {allDocuments.map((doc, idx) => (
                  <Chip
                    key={idx}
                    icon={<DescriptionIcon sx={{ fontSize: '13px !important' }} />}
                    label={doc.filename || `Doc ${idx + 1}`}
                    size="small"
                    clickable
                    color={selectedDocIndex === idx ? 'primary' : 'default'}
                    variant={selectedDocIndex === idx ? 'filled' : 'outlined'}
                    onClick={() => setSelectedDocIndex(idx)}
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Stack>
            ) : filename ? (
              <Chip
                icon={<DescriptionIcon sx={{ fontSize: '13px !important', color: 'primary.main' }} />}
                label={filename}
                size="small"
                variant="outlined"
                color="primary"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  bgcolor: '#ffffff',
                }}
              />
            ) : null}
            {showMimeType && resolvedMimeType && (
              <Chip
                label={resolvedMimeType}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  bgcolor: '#ffffff',
                  borderColor: '#cbd5e1',
                  color: '#475569',
                }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
            {detectedDocument && isDocumentViewable && (
              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={(_, nextMode) => {
                  if (nextMode) setViewMode(nextMode);
                }}
                sx={{ height: 22 }}
              >
                <ToggleButton
                  value="document"
                  sx={{ px: 0.75, py: 0, fontSize: '0.65rem', textTransform: 'none', lineHeight: 1 }}
                >
                  Document
                </ToggleButton>
                <ToggleButton
                  value="raw"
                  sx={{ px: 0.75, py: 0, fontSize: '0.65rem', textTransform: 'none', lineHeight: 1 }}
                >
                  Raw JSON
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {detectedDocument && (
              <Tooltip title={`Download ${filename || 'Document'}`}>
                <IconButton size="small" onClick={handleDownload} sx={{ p: 0.25 }}>
                  <DownloadIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}

            {allowCopy && (
              <Tooltip title={copied ? 'Copied!' : 'Copy Payload'}>
                <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            {headerActions}
          </Stack>
        </Stack>
      )}

      {/* Viewer Dispatcher */}
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'auto', boxSizing: 'border-box' }}>
        {detectedDocument && isDocumentViewable && viewMode === 'raw' ? (
          <JsonViewer
            json={typeof rawInput === 'object' ? rawInput : parseJson(rawInput) || rawInput}
            maxHeight={maxHeight}
            showLabel={false}
          />
        ) : detectedDocument && !isDocumentViewable ? (
          <Box>
            <Box
              sx={{
                p: 1.5,
                mb: 1,
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <DescriptionIcon sx={{ fontSize: 28, color: '#64748b' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {filename || 'Document'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detectedDocument.mimeType} • No inline viewer available
                  </Typography>
                </Box>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                sx={{ textTransform: 'none', fontSize: '0.75rem', flexShrink: 0, ml: 1 }}
              >
                Download
              </Button>
            </Box>
            <JsonViewer
              json={typeof rawInput === 'object' ? rawInput : parseJson(rawInput) || rawInput}
              maxHeight={maxHeight}
              showLabel={false}
            />
          </Box>
        ) : viewerKind === 'json' ? (
          <JsonViewer
            json={
              typeof resolvedContent === 'object'
                ? resolvedContent
                : parseJson(resolvedContent) || resolvedContent
            }
            maxHeight={maxHeight}
            showLabel={false}
          />
        ) : viewerKind === 'xml' ? (
          <XmlFileViewer xmlContent={String(resolvedContent)} maxHeight={maxHeight} showLabel={false} />
        ) : viewerKind === 'csv' ? (
          <CsvViewer csvContent={String(resolvedContent)} maxHeight={maxHeight} showLabel={false} />
        ) : viewerKind === 'image' ? (
          <ImageViewer imageDataUrl={String(resolvedContent)} maxHeight={maxHeight} showLabel={false} />
        ) : viewerKind === 'audio' ? (
          <AudioPlayer audioDataUrl={String(resolvedContent)} maxHeight={maxHeight} showLabel={false} />
        ) : viewerKind === 'video' ? (
          <VideoViewer videoDataUrl={String(resolvedContent)} maxHeight={maxHeight} showLabel={false} />
        ) : viewerKind === 'pdf' ? (
          <PdfViewer pdfDataUrl={String(resolvedContent)} maxHeight={maxHeight || 500} showLabel={false} />
        ) : (
          <TextViewer
            content={String(resolvedContent)}
            maxHeight={maxHeight}
            showLabel={false}
            useMarkdown={true}
          />
        )}
      </Box>
    </Box>
  );
};

export default EventPayloadViewer;
